import React, { useState, useCallback, useMemo } from 'react';
import { Heart, Trash2, Film } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import MovieCard from '@/components/ui/MovieCard';
import MovieModal from '@/components/ui/MovieModal';
import { useFavoritesContext } from '@/context/FavoritesContext';
import { movies } from '@/data/movies';
import { cn } from '@/lib/utils';

const FavoritesPage = () => {
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { favorites, removeFavorite } = useFavoritesContext();

  // Get favorite movies data
  const favoriteMovies = useMemo(() => {
    return movies.filter((movie) => favorites.includes(movie.id));
  }, [favorites]);

  // Handle showing movie details
  const handleShowDetails = useCallback((movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  }, []);

  // Handle closing modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedMovie(null), 300);
  }, []);

  // Handle clear all favorites
  const handleClearAll = useCallback(() => {
    favorites.forEach((id) => removeFavorite(id));
  }, [favorites, removeFavorite]);

  return (
    <main className="min-h-screen bg-background pt-24 lg:pt-28">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-6 h-6 text-primary fill-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                Mi Lista
              </h1>
              <p className="text-muted-foreground">
                {favoriteMovies.length} {favoriteMovies.length === 1 ? 'película guardada' : 'películas guardadas'}
              </p>
            </div>
          </div>

          {favoriteMovies.length > 0 && (
            <Button
              variant="outline"
              onClick={handleClearAll}
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
            >
              <Trash2 className="w-4 h-4" />
              <span>Vaciar Lista</span>
            </Button>
          )}
        </div>

        {/* Content */}
        {favoriteMovies.length > 0 ? (
          <>
            {/* Movies Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 md:gap-6">
              {favoriteMovies.map((movie, index) => (
                <MovieCard
                  key={movie.id}
                  movie={movie}
                  onShowDetails={handleShowDetails}
                  index={index}
                />
              ))}
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-24 h-24 rounded-full bg-secondary/50 flex items-center justify-center mb-6">
              <Film className="w-12 h-12 text-muted-foreground" />
            </div>
            <h3 className="text-2xl font-semibold text-foreground mb-3">
              Tu lista está vacía
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Guarda tus películas favoritas para verlas más tarde. Haz clic en el ícono de corazón en cualquier película para agregarla a tu lista.
            </p>
            <Link to="/movies">
              <Button size="lg" className="gap-2">
                <Film className="w-5 h-5" />
                <span>Explorar Películas</span>
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Movie Detail Modal */}
      <MovieModal
        movie={selectedMovie}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </main>
  );
};

export default FavoritesPage;
